/**
 * Ports `COMAPNY_SIZES` (legacy name misspelled) from
 * `2025-06/risk-analysis-service/routes/schemas/v3/constants.js` — maps a
 * blueprint company's raw `company_size` value to its display label.
 */
export const COMPANY_SIZE_LABELS: Record<string, string> = {
  FROM_0_TO_1: '0 - 1',
  FROM_2_TO_10: '2 - 10',
  FROM_11_TO_50: '11 - 50',
  FROM_51_TO_200: '51 - 200',
  FROM_201_TO_500: '201 - 500',
  FROM_501_TO_1000: '501 - 1000',
  FROM_1001_TO_5000: '1001 - 5000',
  FROM_5001_TO_10000: '5001 - 10000',
  FROM_10001_TO_INFINITE: 'More than 10000',
};

/** Ports `MITIGATION_TYPES_ENUM` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const MITIGATION_TYPES = {
  MANUAL: 'MANUAL',
  AUTOMATIC: 'AUTOMATIC',
} as const;

/** Ports `RULE_OFFSET_TYPE` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const RULE_OFFSET_TYPES = {
  FIXED: 'FIXED',
  OFFSET: 'OFFSET',
} as const;

/**
 * `triggerEvent` values carried on the assessment-mitigation event. Only the members
 * this module emits are ported from `MITIGATION_EVENT` in
 * `2025-06/risk-analysis-service/lib/constants.js`; the legacy object has more.
 */
export const MITIGATION_EVENTS = {
  MANUAL_MITIGATION: 'MANUAL_MITIGATION',
  MANUAL_RECALCULATION: 'MANUAL_RECALCULATION',
} as const;

/** Ports `ASSESSMENT_STATUSES` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const ASSESSMENT_STATUSES = {
  CALCULATING: 'CALCULATING',
  READY: 'READY',
  OUTDATED: 'OUT_DATED',
  FAILED: 'FAILED',
} as const;

/** Ports `RULE_OPERATOR_TYPES` — the `$OP` joining a rule datapoint to the one before it. */
export const RULE_OPERATOR_TYPES = {
  AND: 'AND',
  OR: 'OR',
} as const;

/**
 * When the stale-assessment sweep gives up on a CALCULATING assessment, and
 * how long after creation it may still retry one. Calculating one company
 * takes seconds; half an hour means the message carrying it is gone. See
 * `AssessmentCalculationService.sweepStaleCalculatingAssessments`.
 */
export const STALE_ASSESSMENT_SWEEP = {
  STALE_AFTER_MINUTES: 30,
  RETRY_WITHIN_HOURS: 24,
} as const;

/** Ports `METRIC_IDS` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const METRIC_IDS = {
  LIKELIHOOD: 'LIKELIHOOD',
  SEVERITY: 'SEVERITY',
  /** Not a scored metric: a score formula reads it from the OPERATIONAL_METRICS profile. */
  COMPANY_LEVERAGE: 'COMPANY_LEVERAGE',
} as const;

/** Ports `COMPANY_PROFILE_TYPE` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const COMPANY_PROFILE_TYPES = {
  ABOUT: 'ABOUT',
  OPERATIONAL_METRICS: 'OPERATIONAL_METRICS',
} as const;

/**
 * Ports `riskAnalysisDataPointIds.retracedRiskScore` from
 * `2025-06/risk-analysis-service/lib/constants/data-point-Ids.js` — the datapoint
 * a standard assessment's net score must validate against.
 */
export const RETRACED_RISK_SCORE_DATAPOINT_ID = 'retraced_risk_score';

/** Ports `standardTemplateId` from `2025-06/risk-analysis-service/lib/constants/rule-template.js`. */
export const STANDARD_TEMPLATE_ID = 'RETRACED_STANDARD_TEMPLATE';

/** Ports `standardBlueprintName` from `lib/constants/assessment-bluprint.js`. */
export const STANDARD_BLUEPRINT_NAME = 'Retraced Standard Assessment';

/** Ports `blueprintTypes.raw` from `lib/constants/assessment-bluprint.js`. */
export const BLUEPRINT_TYPES = {
  SYSTEM: 'SYSTEM',
  USER: 'USER',
} as const;

/** Ports `RULE_ASSET_TYPES` from `2025-06/risk-analysis-service/lib/constants.js`. */
export const RULE_ASSET_TYPES = {
  AUDITS: 'AUDITS',
  AUDIT_FINDINGS: 'AUDIT_FINDINGS',
  CERTIFICATES: 'CERTIFICATES',
  COMPANY_DOCUMENTS: 'COMPANY_DOCUMENTS',
  COMPANY_PROFILES: 'COMPANY_PROFILES',
  FACILITY_PROCESSES: 'FACILITY_PROCESSES',
  RELATIONSHIP_TYPE: 'RELATIONSHIP_TYPE',
  PRODUCT_TYPES: 'PRODUCT_TYPES',
} as const;

/**
 * Ports `EVENT_DATAPOINT_MAP` from `2025-06/risk-analysis-service/lib/constants.js` —
 * maps a mitigation `triggerEvent` to the datapoint-trigger key it exercises, used
 * to filter a rule template down to the rules a given event can match.
 */
export const EVENT_DATAPOINT_MAP: Record<string, string> = {
  PROFILE_UPDATED: 'COMPANY_PROFILES',
  DOCUMENT_ARCHIVED: 'COMPANY_DOCUMENTS',
  DOCUMENT_SHARED: 'COMPANY_DOCUMENTS',
  DOCUMENT_UPDATED: 'COMPANY_DOCUMENTS',
  DOCUMENT_UPLOADED: 'COMPANY_DOCUMENTS',
  CERTIFICATE_ARCHIVED: 'CERTIFICATES',
  CERTIFICATE_DELETED: 'CERTIFICATES',
  CERTIFICATE_EXPIRED: 'CERTIFICATES',
  CERTIFICATE_UPDATED: 'CERTIFICATES',
  CERTIFICATE_UPLOADED: 'CERTIFICATES',
  AUDITS_ACCESS_SHARED: 'AUDITS',
  AUDITS_ACCESS_REMOVED: 'AUDITS',
  AUDITS_ACCESS_UPDATED: 'AUDITS',
  AUDITS_WITH_FINDINGS_ACCESS_SHARED: 'AUDIT_FINDINGS',
  AUDITS_WITH_FINDINGS_ACCESS_REMOVED: 'AUDIT_FINDINGS',
  AUDITS_WITH_FINDINGS_ACCESS_UPDATED: 'AUDIT_FINDINGS',
};

/**
 * Ports `COMPANY_PROFILE_DATAPOINTS_MAP` from
 * `2025-06/risk-analysis-service/lib/constants.js` — labels for the company
 * profile datapoint keys a `PROFILE_UPDATED` assessment trigger's `asset` can
 * carry (one boolean flag per changed datapoint, no `type`/`typeId`), used to
 * derive `assessmentTrigger.assetName` as an array of labels in that case.
 */
export const COMPANY_PROFILE_DATAPOINTS_MAP: Record<string, string> = {
  facility_processes: 'Facility Processes',
  company_size: 'Company Size',
  product_types: 'Product Types',
  number_of_production_sites: 'Number of Production Sites',
  relationship_type: 'Relationship Type',
  collaboration_status: 'Collaboration Status',
};

/** Ports `RULE_TEMPLATE_VISIBILITIES_ENUM` from `2025-06/risk-analysis-service/routes/schemas/v3/constants.js`. */
export const RULE_TEMPLATE_VISIBILITIES = {
  PRIVATE: 'PRIVATE',
  RETRACED: 'RETRACED',
} as const;

/** Ports `RULE_TEMPLATE_STATUS_ENUM` from `2025-06/risk-analysis-service/routes/schemas/v3/constants.js`. */
export const RULE_TEMPLATE_STATUS = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
} as const;

/** Ports `RULE_TEMPLATE_TYPE_ENUM` from `2025-06/risk-analysis-service/routes/schemas/v3/constants.js`. */
export const RULE_TEMPLATE_TYPES = {
  SYSTEM: 'SYSTEM',
  USER: 'USER',
} as const;

/**
 * Ports `AUDITS_WITH_FINDINGS` from
 * `2025-06/risk-analysis-service/lib/constants/audits.js` — the audit-type
 * ids the risk rule builder's AUDIT_FINDINGS asset picker restricts to
 * (`GET /risk-analysis/audit-types?withFindings=true`). A risk-analysis
 * business rule about the `audits_service_audit_types` catalogue, not a
 * property of the table itself — hence it lives here, not in `modules/audits`.
 */
export const AUDITS_WITH_FINDINGS = [
  'BSCI',
  'SEDEX_SMETA',
  'FAIR_WEAR_FOUNDATION',
] as const;
